// Area: artefacts (owner artefact-store in wave 1, design-system in wave 2). Stored themes (plan §8.14):
// the starter kit's themes in every workspace (read-only) plus the workspace's own. Documents refer to a
// theme by its `key` (`themeId` of canvas and document templates), which never changes.
import { z } from 'zod';
import { LocalizedText, localizedText } from '@dataroom/core/localized-text';
import { Theme } from '@dataroom/core/doc/theme';
import { q } from '../query';
import { defineContract } from '../define';

export const ThemeOrigin = z.enum(['starter', 'blueprint', 'workspace']);
export type ThemeOrigin = z.infer<typeof ThemeOrigin>;

export const ThemeDTO = z.object({
  id: z.uuid(),
  /** Stable key that documents store (`graphite`, `brand-2026`). */
  key: z.string(),
  /** Name in the member's language. */
  name: z.string(),
  description: z.string().nullable(),
  origin: ThemeOrigin,
  theme: Theme,
  dark: z.boolean(),
  version: z.number().int(),
  updatedAt: z.string(),
  /** The member may change it (workspace themes, with the right to configure the workspace). */
  canEdit: z.boolean(),
  archived: z.boolean(),
});
export type ThemeDTO = z.infer<typeof ThemeDTO>;

/** A theme's definition without the identity the store assigns (key) or keeps separately (name). */
export const ThemeDefinitionInput = Theme.omit({ key: true, name: true });
export type ThemeDefinitionInput = z.input<typeof ThemeDefinitionInput>;

const ThemeName = localizedText({ minLength: 1, maxLength: 80 });

export const listThemes = defineContract({
  method: 'GET',
  path: '/themes',
  query: z.object({ archived: q.bool().optional() }),
  response: z.array(ThemeDTO),
  summary: 'Themes of the workspace: the starter kit, blueprints and the workspace’s own',
});

export const getTheme = defineContract({
  method: 'GET',
  path: '/themes/:id',
  params: z.object({ id: z.uuid() }),
  response: ThemeDTO,
  summary: 'One theme',
});

export const CreateThemeBody = z
  .object({
    name: ThemeName,
    description: LocalizedText.optional(),
    /** Start from a copy of this theme (a starter theme to customise). */
    from: z.uuid().optional(),
    /** The definition; with `from`, it replaces the copied one. One of the two is required. */
    theme: ThemeDefinitionInput.optional(),
  })
  .strict()
  .refine((b) => b.from !== undefined || b.theme !== undefined, {
    message: 'Choose a theme to start from or send a definition',
    path: ['theme'],
  });

export const createTheme = defineContract({
  method: 'POST',
  path: '/themes',
  body: CreateThemeBody,
  response: ThemeDTO,
  status: 201,
  summary: 'Create a theme, or duplicate one to customise it',
});

export const UpdateThemeBody = z
  .object({
    version: z.number().int().positive(),
    name: ThemeName.optional(),
    description: LocalizedText.nullable().optional(),
    theme: ThemeDefinitionInput.optional(),
    archived: z.boolean().optional(),
  })
  .strict();

export const updateTheme = defineContract({
  method: 'PATCH',
  path: '/themes/:id',
  params: z.object({ id: z.uuid() }),
  body: UpdateThemeBody,
  response: ThemeDTO,
  summary: 'Change a workspace theme (name, colours, fonts, text styles) or archive it',
});
